import { Helmet, HelmetProvider } from "react-helmet-async";
import React from "react";
import { BrowserRouter as Router, Routes, Route, useLocation } from "react-router-dom";


import { ToastProvider } from "./context/ToastContext";

import Home from "./pages/Home";
import Plans from "./pages/Plans";
import MedicareAdvantage from "./pages/MedicareAdvantage";
import PartDPlans from "./pages/PartDPlans";
import MedicareSupplements from "./pages/MedicareSupplements";

import Questionnaire from "./pages/Questionnaire";
import AdminPanel from "./pages/admin/AdminPanel";
import AdminLogin from "./pages/AdminLogin";
import AdminSignup from "./pages/AdminSignup";

import About from "./pages/About";          // ← new
import Brokers from "./pages/Brokers";      // ← new (create a simple page if you don't have one yet)
import Blog from "./pages/Blog";
import BlogPost from "./pages/BlogPost";
import PrivateRoute from "./components/PrivateRoute";
import Help from "./pages/Help.js";
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";
import Contact from "./pages/Contact";
import TermLife from "./pages/TermLife";
import PermanentLife from "./pages/PermanentLife";
import HowToBecomePartner from "./pages/HowToBecomePartner";
import PartnerApply from "./pages/PartnerApply";
import Privacy from "./pages/Privacy";
import Terms from "./pages/Terms";
import SMSTerms from "./pages/SMSTerms";
import FAQPage from "./pages/FAQ.js";
import MedicareBasics from "./pages/MedicareBasics";
import EnterZip from "./pages/EnterZip";
import HealthInsurance from "./pages/HealthInsurance";
 import MatchedBrokers from "./pages/MatchBrokers";
 import BrokerLogin from "./pages/BrokerLogin.jsx";
 import BrokerSignup from "./pages/BrokerSignup.jsx";
 import BrokerRoute from "./components/BrokerRoute.jsx";
 import BrokerDashboard from "./pages/BrokerDashboard.jsx";
/** Scroll to top on route change */
function ScrollToTop() {
  const { pathname } = useLocation();
  React.useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: "instant" });
  }, [pathname]);
  return null;
}

/** Very small 404 fallback (replace with your own page if desired) */
function NotFound() {
  return (
    <div className="min-h-[60vh] flex items-center justify-center text-center px-6">
      <div>
        <h1 className="text-4xl font-extrabold text-[#002b5c]">Page not found</h1>
        <p className="text-gray-600 mt-2">The page you’re looking for doesn’t exist.</p>
        <a href="/" className="inline-block mt-6 bg-[#002b5c] text-white px-6 py-3 rounded-full font-semibold hover:bg-[#001b35] transition">
          Go Home
        </a>
      </div>
    </div>
  );
}

export default function App() {
  return (
    <HelmetProvider>
     <Helmet
  defaultTitle="Your Choice Associates"
  titleTemplate="%s | Your Choice Associates"
>
    {/* Favicon + iOS icon */}
    <link rel="icon" type="image/png" href="/images/small-logo.png" sizes="any" />
    <link rel="apple-touch-icon" href="/images/small-logo.png" />
    {/* (Optional) PWA manifest if you add one */}
    {/* <link rel="manifest" href="/site.webmanifest" /> */}
    <meta name="theme-color" content="#002b5c" />
 
  {/* Basic SEO */}
  <meta name="description" content="Your Choice Associates connects you with licensed insurance brokers for no-cost guidance on Medicare (Advantage, Part D, Medigap) and Life Insurance (term & permanent). Compare plans, get answers, and feel confident in your choice." />
  <meta name="keywords" content="Your Choice Associates, Medicare Advantage, Part D, Medigap, Medicare plans, life insurance, term life, whole life, permanent life, licensed brokers, insurance help" />
  <meta name="author" content="Your Choice Associates" />
  <meta name="publisher" content="Your Choice Associates" />
  <meta name="robots" content="index,follow,max-image-preview:large" />
  <link rel="canonical" href="https://yourchoiceassociates.org/" />

  {/* Open Graph (Facebook/LinkedIn/etc.) */}
  <meta property="og:type" content="website" />
  <meta property="og:site_name" content="Your Choice Associates" />
  <meta property="og:title" content="Your Choice Associates" />
  <meta property="og:description" content="Licensed brokers, clear answers. Compare Medicare & Life Insurance with confidence." />
  <meta property="og:url" content="https://yourchoiceassociates.org/" />
  <meta property="og:image" content="/images/medicare-101.jpg" />
  <meta property="og:image:width" content="1200" />
  <meta property="og:image:height" content="630" />
  <meta property="og:image:alt" content="Your Choice Associates — helpful guidance from licensed brokers" />
  <meta property="og:locale" content="en_US" />

  {/* Twitter Card */}
  <meta name="twitter:card" content="summary_large_image" />
  <meta name="twitter:title" content="Your Choice Associates" />
  <meta name="twitter:description" content="Licensed brokers, clear answers. Compare Medicare & Life Insurance with confidence." />
  <meta name="twitter:image" content="/images/medicare-101.jpg" />

  {/* Organization Schema.org (Google knowledge panels, rich results) */}
  <script type="application/ld+json">
    {JSON.stringify({
      "@context": "https://schema.org",
      "@type": "Organization",
      "name": "Your Choice Associates",
      "url": "https://yourchoiceassociates.org/",
      "logo": "https://yourchoiceassociates.org/images/small-logo.png",
      "description":
        "Your Choice Associates connects consumers with licensed brokers for no-cost guidance on Medicare (Advantage, Part D, Medigap) and Life Insurance.",
      "contactPoint": [
        {
          "@type": "ContactPoint",
          "telephone": "+1-917-902-4471",
          "contactType": "customer support",
          "areaServed": "US",
          "availableLanguage": ["English"]
        }
      ],
      "sameAs": [] // add social URLs here later if you have them
    })}
  </script>
  </Helmet>
      <ToastProvider>
        <Router>
          <ScrollToTop />
          <Routes>
            <Route path="/" element={<Home />} />
            <Route path="/plans" element={<Plans />} />
            <Route path="/questionnaire" element={<Questionnaire />} />

            {/* Public pages */}
            <Route path="/about" element={<About />} />
            <Route path="/brokers" element={<Brokers />} />
            <Route path="/contact" element={<Contact />} /> 
            <Route path="/medicare-a" element={<MedicareAdvantage />} />
            <Route path="/medicare-d" element={<PartDPlans />} />
            <Route path="/medicare-supplements" element={<MedicareSupplements />} />
            <Route path="/term-life" element={<TermLife />} />
            <Route path="/permanent-life" element={<PermanentLife />} />
            <Route path="/how-to-become-partner" element={<HowToBecomePartner />} />
            <Route path="/partner-apply" element={<PartnerApply />} />
            <Route path="/privacy" element={<Privacy />} />
            <Route path="/terms" element={<Terms />} />
            <Route path="/sms-terms" element={<SMSTerms />} />
            <Route path="/blog" element={<Blog />} />
            <Route path="/blog/:slug" element={<BlogPost />} />
            <Route path="/matched-brokers" element={<MatchedBrokers />} />

            <Route path="/broker-login" element={<BrokerLogin />} />
            <Route path="/broker-signup" element={<BrokerSignup />} />
            <Route
              path="/broker/dashboard"
              element={
                <BrokerRoute>
                  <BrokerDashboard />
                </BrokerRoute>
              }
            />

            <Route path="/start" element={<EnterZip />} />
            <Route path="/health-insurance" element={<HealthInsurance />} />
             <Route path="/help" element={<Help />} />
            <Route path="/faq" element={<FAQPage />} />
            <Route path="/help/medicare-basics" element={<MedicareBasics />} />
            {/* Admin (protected) — broker application review now lives inside AdminPanel as a tab */}
            <Route
              path="/admin"
              element={
                <PrivateRoute>
                  <AdminPanel />
                </PrivateRoute>
              }
            />
            <Route path="/admin-login" element={<AdminLogin />} />
            <Route path="/admin-signup" element={<AdminSignup />} />

            {/* 404 */}
            <Route path="*" element={<NotFound />} />
            
          </Routes>
        </Router>
      </ToastProvider>
    </HelmetProvider>
  );
}